import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { sortTeeth, type Kit } from '@/domain';
import { strings } from '@/shared/strings';
import { Odontogram, SelectionCaption } from '@/ui/data/Odontogram';
import { SpanList } from '@/ui/data/SpanList';
import { RotateIcon } from '@/ui/icons';
import { useMediaQuery } from '@/ui/layout/useMediaQuery';
import { FullScreenPage } from '@/ui/primitives/FullScreenPage';
import { DURATION, EASE, EXIT, reducedMotion } from '@/ui/motion/motion';
import { chartFitScale, turnHelps, type Size } from './chartFit';
import styles from './TeethViewer.module.css';

const MIN = 1;
const MAX = 4;

/** Kept clear between the chart and the edge of the stage: 8 pixels a side. */
const STAGE_INSET = 16;

/**
 * A phone lying on its side. There is no height there for a header, a chart,
 * a list of bridges and a footer, so the list and the footer go and the chart
 * has what they took.
 */
const SHORT_LANDSCAPE = '(orientation: landscape) and (max-height: 500px)';

/** The room the stage has for the chart, and the size the chart is laid out at. */
interface Room {
  stage: Size;
  chart: Size;
}

const sameSize = (a: Size, b: Size) => a.width === b.width && a.height === b.height;

const screenSize = (): Size => ({ width: window.innerWidth, height: window.innerHeight });

/**
 * `lock` is in the Screen Orientation specification and in Chrome on Android,
 * but TypeScript's DOM library declares `unlock` alone.
 */
interface OrientationLock {
  lock?: (orientation: 'any') => Promise<void>;
  unlock?: () => void;
}

/**
 * Lets the screen turn with the phone while the chart is open. The request of
 * 5 October 2026 (third), section 5.
 *
 * The installed app is held upright by its manifest, which is right for every
 * other screen. Where the browser allows it, the hold is lifted for as long as
 * the chart is open and put back when it closes. An attempt and no more: an
 * iPhone, a desktop and a browser tab refuse or have no such thing, every
 * refusal is swallowed, and those people have the Rotate button. The hold is
 * only given back if it was really taken.
 */
function useScreenFreeToTurn(): void {
  useEffect(() => {
    const orientation = (typeof screen === 'undefined' ? undefined : screen.orientation) as
      | OrientationLock
      | undefined;
    if (orientation === undefined || typeof orientation.lock !== 'function') return undefined;

    let taken = false;
    let closed = false;
    const giveBack = () => {
      try {
        orientation.unlock?.();
      } catch {
        // Nothing was held after all.
      }
    };

    try {
      orientation.lock('any').then(
        () => {
          // Answered after the chart had closed: there is nobody left to give it back.
          if (closed) giveBack();
          else taken = true;
        },
        () => undefined,
      );
    } catch {
      // Some browsers throw where the specification says to reject.
    }

    return () => {
      closed = true;
      if (taken) giveBack();
    };
  }, []);
}

/**
 * The teeth chart over the whole screen, to zoom into. Request of 4 October
 * 2026 (second), section 8.
 *
 * Pinch, or the buttons, to zoom; drag to move about once zoomed; the legend
 * says what the colours and marks mean; the cross returns to the case as it
 * was. The same chart the case and the intake form draw, larger.
 *
 * Since the request of 5 October 2026 (third), section 5, the chart is scaled
 * to fit the screen it is on, and Rotate turns it a quarter clockwise so that
 * it fills a phone held upright; a second press turns it back. The turn is on
 * a wrapper inside the one that is zoomed and dragged, so a pinch and a drag
 * work on the screen's own axes whichever way the chart faces. When the phone
 * itself is turned the chart is fitted again, and comes upright if turning it
 * no longer helps. The turn is not remembered: every opening starts upright.
 */
export function TeethViewer({
  kit,
  onClose,
  from = null,
}: {
  /** The case's teeth, its bridges and bars, and its number: all the viewer reads. */
  kit: Pick<Kit, 'jobOrderNo' | 'teeth' | 'spans'>;
  onClose: () => void;
  /**
   * Where the small chart was on the case, so the large one grows out of it and
   * shrinks back into it: one chart getting bigger, not a new screen. Request of
   * 5 October 2026, section 7.
   */
  from?: DOMRect | null;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const fly = useRef<HTMLDivElement>(null);
  const rotor = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ distance: number; zoom: number } | null>(null);

  /** Turned a quarter clockwise. Per opening, and not kept anywhere. */
  const [turned, setTurned] = useState(false);
  /** The turn is animated only once Rotate has been pressed, and not after the screen itself changed. */
  const [turning, setTurning] = useState(false);
  const [room, setRoom] = useState<Room | null>(null);
  const measured = useRef<Room | null>(null);
  /** When Rotate was last pressed. A change of the stage while that turn plays is the turn's own doing. */
  const pressedAt = useRef(Number.NEGATIVE_INFINITY);
  /** The screen the chart was opened on. The place of the small chart was measured on it. */
  const [openedOn] = useState(screenSize);
  const isTurned = useRef(turned);
  isTurned.current = turned;

  const lying = useMediaQuery(SHORT_LANDSCAPE);
  useScreenFreeToTurn();

  const fit = room === null ? 1 : chartFitScale(room.stage, room.chart, turned);
  // Until something is measured the button is there: a test has no layout at all.
  const helps = room === null || turnHelps(room.stage, room.chart);

  const clamp = (value: number) => Math.min(MAX, Math.max(MIN, value));
  const zoomTo = (value: number) => {
    const next = clamp(value);
    setZoom(next);
    if (next === MIN) setOffset({ x: 0, y: 0 });
  };

  function down(event: React.PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a !== undefined && b !== undefined) gesture.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom };
    }
  }

  function move(event: React.PointerEvent<HTMLDivElement>) {
    const before = pointers.current.get(event.pointerId);
    if (before === undefined) return;
    const now = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, now);
    const pinch = gesture.current;
    if (pinch !== null && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a !== undefined && b !== undefined && pinch.distance > 0) {
        zoomTo(pinch.zoom * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.distance));
      }
      return;
    }
    if (zoom > MIN) setOffset((o) => ({ x: o.x + now.x - before.x, y: o.y + now.y - before.y }));
  }

  /*
   * The fit. The stage is measured less its inset and the chart by its layout
   * size, which no transform changes, on opening and again whenever either
   * changes size: the phone turned, a window dragged, the list of bridges
   * leaving the page as the chart turns.
   */
  useLayoutEffect(() => {
    const stageNode = stage.current;
    const rotorNode = rotor.current;
    if (stageNode === null || rotorNode === null) return undefined;

    const measure = () => {
      const next: Room = {
        stage: {
          width: stageNode.clientWidth - STAGE_INSET,
          height: stageNode.clientHeight - STAGE_INSET,
        },
        chart: { width: rotorNode.offsetWidth, height: rotorNode.offsetHeight },
      };
      const before = measured.current;
      if (before !== null && sameSize(before.stage, next.stage) && sameSize(before.chart, next.chart)) return;
      measured.current = next;
      setRoom(next);
      if (before === null) return;

      if (performance.now() - pressedAt.current < DURATION.normal) {
        // Rotate was just pressed and the list of bridges left the page, or came back to it:
        // the stage's centre moved, and the chart slides to it as it turns.
        slide(fly.current, before.stage, next.stage);
      } else {
        // The screen changed, and a phone animates its own turn: the new fit is a cut.
        setTurning(false);
      }
      // Turned by hand to fill an upright phone, and the phone is on its side now.
      if (isTurned.current && !turnHelps(next.stage, next.chart)) {
        setTurned(false);
        setZoom(MIN);
        setOffset({ x: 0, y: 0 });
      }
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(stageNode);
    observer.observe(rotorNode);
    return () => observer.disconnect();
  }, []);

  /** The transform that puts the large chart where the small one was. */
  function fromSmall(): string | null {
    const frame = fly.current;
    const chart = rotor.current;
    if (from === null || frame === null || chart === null || typeof frame.animate !== 'function' || reducedMotion()) {
      return null;
    }
    const to = chart.getBoundingClientRect();
    if (to.width === 0 || to.height === 0) return null;
    const around = frame.getBoundingClientRect();
    const scale = from.width / to.width;
    // The frame scales about its own centre, which is the chart's centre too until the chart is dragged.
    const centreX = around.left + around.width / 2;
    const centreY = around.top + around.height / 2;
    const dx = from.left + from.width / 2 - centreX - scale * (to.left + to.width / 2 - centreX);
    const dy = from.top + from.height / 2 - centreY - scale * (to.top + to.height / 2 - centreY);
    return `translate(${String(dx)}px, ${String(dy)}px) scale(${String(scale)})`;
  }

  /*
   * Opening: the chart grows out of the small one. Played on the commit that
   * carries the first fit and not on the mount, where the chart is still its
   * laid out 600 wide and would be measured at the wrong size.
   */
  const fitted = room !== null;
  const grown = useRef(false);
  useLayoutEffect(() => {
    if (!fitted || grown.current) return;
    grown.current = true;
    const start = fromSmall();
    if (start === null) return;
    fly.current?.animate([{ transform: start }, { transform: 'none' }], {
      duration: DURATION.normal,
      easing: EASE.enter,
    });
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitted]);

  /*
   * Closing shrinks it into the case again, a little quicker than it grew. Not
   * when the chart is turned, and not when the screen has changed since it
   * opened: the small chart's place was measured then, and it is elsewhere now.
   */
  function close() {
    const end = turned || !sameSize(openedOn, screenSize()) ? null : fromSmall();
    const animation =
      end === null
        ? null
        : fly.current?.animate([{ transform: 'none' }, { transform: end }], {
            duration: EXIT.normal,
            easing: EASE.exit,
            fill: 'forwards',
          });
    if (animation === null || animation === undefined) onClose();
    else animation.onfinish = () => onClose();
  }

  function up(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  }

  /** A quarter clockwise, or back. Either way the chart starts again fitted and centred. */
  function rotate() {
    pressedAt.current = performance.now();
    setTurned((was) => !was);
    setTurning(true);
    setZoom(MIN);
    setOffset({ x: 0, y: 0 });
  }

  const teeth = sortTeeth(kit.teeth);

  return (
    <FullScreenPage
      title={kit.jobOrderNo}
      kicker={strings.caseView.chartTitle}
      onClose={close}
      closeLabel={strings.caseView.closeChart}
      closeIcon="close"
      action={
        <div className={styles.zoomButtons}>
          <button type="button" aria-label={strings.caseView.zoomOut} onClick={() => zoomTo(zoom - 0.5)} disabled={zoom <= MIN}>
            −
          </button>
          <button type="button" aria-label={strings.caseView.zoomIn} onClick={() => zoomTo(zoom + 0.5)} disabled={zoom >= MAX}>
            +
          </button>
          {helps && (
            <button type="button" aria-label={strings.caseView.rotate} aria-pressed={turned} onClick={rotate}>
              <RotateIcon size={20} />
            </button>
          )}
        </div>
      }
      footer={
        lying ? undefined : (
          <div className={styles.foot}>
            {/* Out here and not under the chart, where it would be scaled down and turned with it. */}
            <SelectionCaption selected={teeth} alone />
            <div className={styles.legend} aria-label={strings.caseView.legend}>
              <span>
                <i className={styles.keySelected} /> {strings.caseView.legendSelected}
              </span>
              <span>
                <i className={styles.keyBridge} /> {strings.caseView.legendBridge}
              </span>
              <span>
                <i className={styles.keyBar} /> {strings.caseView.legendBar}
              </span>
            </div>
            <div className={styles.hintRow}>
              <span>{strings.caseView.pinchHint}</span>
              {zoom > MIN && (
                <button type="button" onClick={() => zoomTo(MIN)}>
                  {strings.caseView.resetZoom}
                </button>
              )}
            </div>
          </div>
        )
      }
    >
      <div className={styles.layout}>
        <div
          ref={stage}
          className={styles.stage}
          data-testid="teeth-zoom"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onDoubleClick={() => zoomTo(zoom > MIN ? MIN : 2)}
        >
          <div ref={fly} className={styles.fly}>
            <div
              className={styles.canvas}
              style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})` }}
            >
              <div
                ref={rotor}
                className={turning ? `${styles.rotor} ${styles.turning}` : styles.rotor}
                data-testid="teeth-rotor"
                style={{ transform: `translate(-50%, -50%) rotate(${turned ? 90 : 0}deg) scale(${fit})` }}
              >
                <Odontogram selected={teeth} readOnly size="picker" spans={kit.spans} />
              </div>
            </div>
          </div>
        </div>
        {/*
          Not while turned, and not on a phone lying on its side: the names are
          on the chart, and the stage has the whole page.
        */}
        {!turned && !lying && <SpanList spans={kit.spans} heading={strings.job.bridges} />}
      </div>
    </FullScreenPage>
  );
}

/**
 * The stage grew or shrank where it stands, so its centre moved by half of
 * that, and the chart with it. This slides the chart from where it was, in
 * step with the turn that caused it, instead of letting it jump.
 */
function slide(frame: HTMLElement | null, before: Size, now: Size): void {
  if (frame === null || typeof frame.animate !== 'function' || reducedMotion()) return;
  const dx = (before.width - now.width) / 2;
  const dy = (before.height - now.height) / 2;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
  frame.animate([{ transform: `translate(${String(dx)}px, ${String(dy)}px)` }, { transform: 'none' }], {
    duration: DURATION.normal,
    easing: EASE.move,
  });
}
